/* eslint-disable import/no-unresolved */
/* eslint-disable import/extensions */

import * as React from "react";
import { I18n } from "i18n-js";
import * as locales from "../../../../locales.json";
import DOMPurify from "dompurify";
const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";
import { datadogRum } from "@datadog/browser-rum";
import styled from "styled-components";

import * as API from "../../../../api/billing";
import Modal from "../../../../components/reusable/reactModal";
import {
  StyledButton,
  StyledButtonContainer,
  StyledModalBody,
  StyledModalContents,
  StyledModalSubHeader,
  StyledModalText,
  StyledLinkSpan,
  StyledParagraph,
} from "../lib/styles";
import { ReactivateModalProps } from "../lib/types";
import { handleErrorsArray } from "../../../../utils";
import { getCardDisplayName } from "./creditCardPaymentMethod";

const StyledHelperWrapper = styled.div`
  margin-top: 4px;
  margin-bottom: 12px;
`;

const StyledDescription = styled(StyledModalText)`
  margin-bottom:16px;
`;

const ReactivateModal = ({
  cardType,
  closeModal,
  companyId,
  fetchData,
  last4,
  modal,
  subscriptionType,
  updateToastMessage,
  userCount,
}: ReactivateModalProps): React.ReactElement => {
  const handleReactivateClick = async (): Promise<void> => {
    try {
      await API.deleteCompanyCancellation(companyId);

      updateToastMessage({
        isOpen: true,
        message: i18n.translate(
          "admin_pages.settings.subscription.reactivate_confirmation"
        ),
        type: "success",
      });
      fetchData();
      closeModal();
    } catch (error) {
      const errors = handleErrorsArray(error);

      datadogRum.addError(errors);
      updateToastMessage({
        isOpen: true,
        message: i18n.translate(
          "admin_pages.settings.subscription.reactivate_error"
        ),
        type: "error",
      });
    }
  };

  const cardDisplay = getCardDisplayName(cardType);

  return (
    <Modal
      ariaLabel="reactivate modal"
      close={closeModal}
      showDialog={modal === "reactivate"}
      testId="reactivate-modal"
      title={i18n.translate(
        "admin_pages.settings.subscription.reactivate_modal_title"
      )}
    >
      <StyledModalBody data-testid="reactivate-modal-body">
        <StyledModalContents>
          <StyledDescription>
            {i18n.translate(
              "admin_pages.settings.subscription.reactivate_payment_section", {cardType: cardDisplay, last4: last4}
            )}
          </StyledDescription>

          <StyledModalSubHeader>
                {i18n.translate(
                  "title.subscription"
                )}
          </StyledModalSubHeader>
          
          <StyledDescription 
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                i18n.translate(
                  "admin_pages.settings.subscription.reactivate_subscription_section_html", 
                  {
                    plan_name: subscriptionType
                  }
                )
              )
            }}
          />

          <StyledModalSubHeader>
            {i18n.translate(
              "admin_pages.settings.subscription.seats"
            )}
          </StyledModalSubHeader>
          <StyledParagraph>
            {i18n.translate(
              "admin_pages.settings.subscription.reactivate_seats_section", {count: userCount}
            )}
          </StyledParagraph>
          
          <StyledHelperWrapper>
            <StyledLinkSpan
               dangerouslySetInnerHTML={{
                __html: i18n.translate(
                  "admin_pages.settings.subscription.add_users_html"
                ),
            }}></StyledLinkSpan>
          </StyledHelperWrapper>
        </StyledModalContents>
        <StyledButtonContainer>
          <StyledButton
            onClick={closeModal}
            size="small"
            testId="cancel-button"
            variant="secondary"
          >
            {i18n.translate("forms.cancel")}
          </StyledButton>
          <StyledButton
            onClick={handleReactivateClick}
            size="small"
            testId="confirm-reactivate-button"
            variant="primary"
          >
            {i18n.translate("admin_pages.settings.subscription.reactivate")}
          </StyledButton>
        </StyledButtonContainer>
      </StyledModalBody>
    </Modal>
  );
};

export default ReactivateModal;
